import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarPlus, Pencil, Plus, RefreshCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge, Empty, PageHeader, SelectField } from "@/components/common";
import { ContractDialog } from "@/components/v3forms";
import { FilesPanel } from "@/components/FilesPanel";
import { api, formatApiError } from "@/lib/api";
import { useList } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import { CONTRACT_STATUS, CONTRACT_TYPES, PERIODS, INTERVENTION_STATUS, fmtDate, fmtMin, fmtPrice } from "@/lib/constants";

const FILTERS = { actif: "Actifs", expiring: "À renouveler / expirés", low_credit: "Crédit faible", preventive_due: "Préventif à programmer",
  brouillon: "Brouillons", suspendu: "Suspendus", resilie: "Résiliés" };

const Credit = ({ cur }) => cur.available == null ? <span className="text-xs text-slate-400">Forfait</span> : (
  <div className="w-full">
    <div className="h-1.5 rounded bg-white/10"><div className={`h-1.5 rounded ${cur.remaining === 0 ? "bg-red-400" : "bg-cyan-400"}`} style={{ width: `${Math.min(100, (cur.consumed / (cur.available || 1)) * 100)}%` }} /></div>
    <p className="mt-1 text-xs text-slate-300">{fmtMin(cur.consumed)} consommées · reste {fmtMin(cur.remaining)}{cur.overage ? ` · dépassement ${fmtMin(cur.overage)}` : ""}</p>
  </div>
);

export function ContractList() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get("status") || "");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { items, reload } = useList("/contracts", { status, q });
  return (
    <>
      <PageHeader title="Contrats de maintenance" subtitle="Suivi des contrats, heures incluses et maintenances préventives">
        <Button onClick={() => setOpen(true)} className="dt-glow" data-testid="new-contract-button"><Plus className="mr-1 h-4 w-4" />Nouveau contrat</Button>
      </PageHeader>
      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative min-w-0 flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, numéro, site…" className="pl-9" data-testid="contract-search-input" /></div>
        <SelectField value={status} onChange={(v) => setStatus(v || "")} options={Object.entries(FILTERS)} allowNone="Tous" testid="contract-filter-select" className="w-full sm:w-60" />
      </div>
      {!items.length && <Empty testid="contracts-empty">Aucun contrat.</Empty>}
      <div className="grid gap-3 md:grid-cols-2" data-testid="contract-list">
        {items.map((c) => (
          <Link key={c.id} to={`/contrats/${c.id}`} className="space-y-2 rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4 hover:border-cyan-400/50" data-testid={`contract-card-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0"><p className="truncate font-medium text-white">{c.name}</p><p className="text-xs text-slate-400">{c.number} · {c.client_name} · {CONTRACT_TYPES[c.type]}</p></div>
              <Badge value={c.effective_status} map={CONTRACT_STATUS} />
            </div>
            <p className="text-xs text-slate-400">{fmtDate(c.start_date)} → {c.end_date ? fmtDate(c.end_date) : "sans fin"}</p>
            <Credit cur={c.current} />
          </Link>
        ))}
      </div>
      <ContractDialog open={open} onOpenChange={setOpen} initial={{}} onSaved={reload} />
    </>
  );
}

const Finance = ({ f }) => (
  <section className="grid grid-cols-2 gap-3 rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4 sm:grid-cols-4" data-testid="contract-finance">
    {[["Montant du contrat", f.amount != null ? `${fmtPrice(f.amount)} / ${f.billing_frequency}` : "—"], ["Revenu contrat à date", fmtPrice(f.contract_revenue)],
      ["Hors contrat facturable", fmtPrice(f.extras.to_bill)], ["Reste à facturer", fmtPrice(f.remaining_to_bill)],
      ["Matériel facturable", fmtPrice(f.extras.materiel)], ["Déplacements", fmtPrice(f.extras.deplacement)], ["Temps passé", fmtMin(f.tracked_minutes)],
      ["Revenu / heure", f.revenue_per_hour != null ? fmtPrice(f.revenue_per_hour) : "—"]].map(([k, v]) => (
      <div key={k}><p className="text-xs text-slate-400">{k}</p><p className="font-mono-dt text-sm text-white">{v}</p></div>
    ))}
  </section>
);

const Periods = ({ periods }) => (
  <section className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="contract-periods">
    <h2 className="mb-2 text-base font-semibold text-white">Heures et interventions par période</h2>
    {[...periods].reverse().map((p) => (
      <details key={p.start} className="border-t border-white/10 py-2" open={p === periods[periods.length - 1]}>
        <summary className="cursor-pointer text-sm text-slate-200">{fmtDate(p.start)} → {fmtDate(p.end)} · {p.count} intervention(s) · {fmtMin(p.consumed)}
          {p.available != null && ` / ${fmtMin(p.available)} · reste ${fmtMin(p.remaining)}`}{p.overage ? <span className="text-red-300"> · dépassement {fmtMin(p.overage)}</span> : null}</summary>
        <ul className="mt-2 space-y-1">
          {p.items.map((it) => (
            <li key={it.id} className="flex flex-wrap justify-between gap-2 text-xs text-slate-300">
              <Link to={`/interventions/${it.id}`} className="text-cyan-200 hover:underline">{fmtDate(it.date)} · {it.title}</Link>
              <span>chrono {fmtMin(it.tracked)} · retenu {fmtMin(it.retained)} · imputé {fmtMin(it.imputed)}{it.manual ? " (corrigé)" : ""}{it.extra ? ` · hors crédit ${fmtMin(it.extra)}` : ""}</span>
            </li>
          ))}
        </ul>
      </details>
    ))}
  </section>
);

const HISTORY = { created: "Création", status: "Changement de statut", coverage: "Équipements couverts", updated: "Modification", renewed: "Renouvellement",
  preventive_generated: "Maintenance préventive créée", hours_adjusted: "Ajustement d'heures", document_added: "Document ajouté", document_removed: "Document retiré" };

export function ContractDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const load = useCallback(() => api.get(`/contracts/${id}/details`).then((r) => setD(r.data)).catch(() => nav("/contrats")), [id, nav]);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const c = d.contract;
  const act = async (fn, ok) => { try { const r = await fn(); toast.success(ok); return r; } catch (e) { toast.error(formatApiError(e)); return null; } };
  const setStatus = (status) => act(() => api.post(`/contracts/${id}/status`, { status }), "Statut mis à jour").then(load);
  const renew = async () => {
    if (!window.confirm("Créer un nouveau contrat (brouillon) reprenant ces conditions ? Rien n'est envoyé ni facturé au client.")) return;
    const r = await act(() => api.post(`/contracts/${id}/renew`), "Contrat renouvelé en brouillon");
    if (r) nav(`/contrats/${r.data.id}`);
  };
  const generate = async () => {
    const r = await act(() => api.post(`/contracts/${id}/preventive/generate`, {}), "Maintenance créée en brouillon");
    if (r) nav(`/interventions/nouvelle?id=${r.data.id}`);
  };
  const isAdmin = user?.role === "admin";
  return (
    <div className="space-y-5" data-testid="contract-detail">
      <Link to="/contrats" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-cyan-300"><ArrowLeft className="h-4 w-4" />Contrats</Link>
      <PageHeader title={c.name} subtitle={`${c.number} · ${c.client_name} · ${CONTRACT_TYPES[c.type]}`}>
        <Badge value={c.effective_status} map={CONTRACT_STATUS} testid="contract-status-badge" />
        <Button variant="secondary" onClick={() => setEdit(true)} data-testid="contract-edit-button"><Pencil className="mr-1 h-4 w-4" />Modifier</Button>
        {isAdmin && <Button variant="secondary" onClick={renew} data-testid="contract-renew-button"><RefreshCcw className="mr-1 h-4 w-4" />Renouveler</Button>}
      </PageHeader>
      {isAdmin && (
        <div className="flex flex-wrap gap-2" data-testid="contract-status-actions">
          {c.status !== "actif" && <Button size="sm" onClick={() => setStatus("actif")} data-testid="contract-activate-button">Activer</Button>}
          {c.status === "actif" && <Button size="sm" variant="secondary" onClick={() => setStatus("suspendu")} data-testid="contract-suspend-button">Suspendre</Button>}
          {c.status !== "resilie" && <Button size="sm" variant="ghost" onClick={() => window.confirm("Résilier ce contrat ?") && setStatus("resilie")} data-testid="contract-terminate-button">Résilier</Button>}
          <Button size="sm" variant="ghost" onClick={() => act(() => api.patch(`/contracts/${id}`, { archived: !c.archived }), c.archived ? "Désarchivé" : "Archivé").then(load)} data-testid="contract-archive-button">{c.archived ? "Désarchiver" : "Archiver"}</Button>
        </div>
      )}
      <section className="grid gap-3 rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4 sm:grid-cols-3" data-testid="contract-info">
        {[["Période", `${fmtDate(c.start_date)} → ${c.end_date ? fmtDate(c.end_date) : "sans fin"}`], ["Crédit", c.hours_included != null ? `${c.hours_included} h / ${PERIODS[c.period].toLowerCase()}${c.carry_over ? " (report)" : ""}` : "—"],
          ["Site", c.site], ["Inclus", c.included], ["Exclusions", c.exclusions], ["Déplacements", c.travel_terms], ["Matériel", c.material_terms], ["Clauses", c.notes]]
          .filter(([, v]) => v).map(([k, v]) => <div key={k}><p className="text-xs text-slate-400">{k}</p><p className="whitespace-pre-line text-sm text-white">{v}</p></div>)}
      </section>
      {c.preventive_enabled && (
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="contract-preventive">
          <p className="text-sm text-slate-200">Maintenance préventive {c.preventive_frequency === "personnalise" ? `tous les ${c.preventive_every_days} jours` : PERIODS[c.preventive_frequency]?.toLowerCase()} · prochaine : {c.preventive_next_date ? fmtDate(c.preventive_next_date) : "—"}</p>
          <Button onClick={generate} disabled={!c.preventive_next_date} data-testid="contract-generate-preventive-button"><CalendarPlus className="mr-1 h-4 w-4" />Préparer l'intervention</Button>
        </section>
      )}
      {d.finance && <Finance f={d.finance} />}
      <Periods periods={d.periods} />
      <section className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="contract-equipment">
        <h2 className="mb-2 text-base font-semibold text-white">Équipements couverts</h2>
        {d.equipment.length ? d.equipment.map((e) => <Link key={e.id} to={`/equipements/${e.id}`} className="mr-3 text-sm text-cyan-200 hover:underline">{e.name || `${e.brand} ${e.model}`}</Link>) : <p className="text-sm text-slate-400">Aucun.</p>}
      </section>
      <FilesPanel ownerType="contract" ownerId={id} title="Documents contractuels" testid="contract-files" />
      <section className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="contract-history">
        <h2 className="mb-2 text-base font-semibold text-white">Historique</h2>
        <ul className="space-y-1 text-xs text-slate-300">
          {d.history.map((h, i) => (
            <li key={i}>{new Date(h.at).toLocaleString("fr-FR")} · {HISTORY[h.action] || h.action}{h.details?.to ? ` : ${CONTRACT_STATUS[h.details.from] || "—"} → ${CONTRACT_STATUS[h.details.to] || h.details.to}` : ""}
              {h.details?.added?.length ? ` · +${h.details.added.length} équipement(s)` : ""}{h.details?.removed?.length ? ` · −${h.details.removed.length} équipement(s)` : ""}{h.user_name ? ` · ${h.user_name}` : ""}</li>
          ))}
        </ul>
      </section>
      <ContractDialog open={edit} onOpenChange={setEdit} initial={c} onSaved={load} />
    </div>
  );
}

export const ContractPanel = ({ interventionId, contractId }) => {
  const [d, setD] = useState(null);
  const [val, setVal] = useState("");
  const load = useCallback(() => contractId && api.get(`/interventions/${interventionId}/contract`).then((r) => { setD(r.data); setVal(r.data.manual_minutes ?? ""); }), [interventionId, contractId]);
  useEffect(() => { load(); }, [load]);
  if (!contractId || !d?.contract) return null;
  const c = d.contract;
  const save = async () => {
    const minutes = val === "" ? null : Number(val);
    if (minutes === d.manual_minutes) return;
    const reason = window.prompt("Motif de la correction (facultatif)") || "";
    await api.put(`/interventions/${interventionId}/contract-minutes`, { minutes, reason });
    toast.success("Heures imputées mises à jour");
    load();
  };
  return (
    <section className="space-y-2 rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4" data-testid="intervention-contract-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to={`/contrats/${c.id}`} className="font-medium text-cyan-200 hover:underline">Sous contrat : {c.name} ({c.number})</Link>
        <Badge value={c.effective_status} map={CONTRACT_STATUS} />
      </div>
      {c.current.available != null && <Credit cur={c.current} />}
      {d.allocation && <p className="text-sm text-slate-200" data-testid="contract-allocation">Imputé : {fmtMin(d.allocation.imputed)} · couvert {fmtMin(d.allocation.covered)}
        {d.allocation.extra ? <span className="text-amber-300"> · hors crédit facturable {fmtMin(d.allocation.extra)}</span> : null}</p>}
      {!!d.uncovered_equipment.length && <p className="text-xs text-amber-300">Un équipement de cette intervention n'est pas couvert par le contrat.</p>}
      <label className="flex flex-wrap items-center gap-2 text-xs text-slate-300">Minutes imputées (vide = temps retenu)
        <Input type="number" min={0} value={val} onChange={(e) => setVal(e.target.value)} onBlur={save} className="h-8 w-28" data-testid="contract-minutes-input" /></label>
    </section>
  );
};

export const statusOf = (i) => INTERVENTION_STATUS[i];
